import {
  Box,
  Divider,
  FormHelperText,
  Grid2 as Grid,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { InvoiceAdjustmentPayload, ModalForm } from "../../../types";
import { TextField } from "../../common";
import { useValidateForm } from "../../../hooks";
import { discountSchema } from "../../../validations";
import { useState } from "react";
import { formatCurrency } from "../../../utils";

type FormData = InvoiceAdjustmentPayload;
type AdjustmentsFormProps = ModalForm<number, FormData>;

export const AdjustmentsForm = ({
  id,
  defaultValues,
  handleEdit,
}: AdjustmentsFormProps) => {
  const computeValue = (
    discountType: "amount" | "percentage",
    value: string,
  ) => {
    const val = Number(value);
    const result =
      discountType === "amount" ? val : (invoiceAmount * val) / 100;
    return Math.round(result * 100) / 100;
  };

  const invoiceAmount = defaultValues || 0;
  const [discountValue, setDiscountValue] = useState<string>("");
  const [discountType, setDiscountType] = useState<"amount" | "percentage">(
    "amount",
  );

  const discountApplied = computeValue(discountType, discountValue);

  const [feeValue, setFeeValue] = useState<string>("");
  const [feeType, setFeeType] = useState<"amount" | "percentage">("amount");
  const feeApplied = computeValue(feeType, feeValue);

  const finalAmount = invoiceAmount - discountApplied + feeApplied;

  const { register, handleSubmit, errors } = useValidateForm(
    discountSchema
      .refine(
        (data) =>
          !(discountType === "amount") ||
          Number(data.discountAmount) <= invoiceAmount,
        {
          message: `Discount cannot exceed invoice amount`,
          path: ["discountAmount"],
        },
      )
      .refine(
        (data) =>
          !(discountType === "percentage") ||
          Number(data.discountAmount) <= 100,
        {
          message: `Discount cannot be more than 100%`,
          path: ["discountAmount"],
        },
      ),
  );

  const shouldShowDiscountPreview =
    discountValue &&
    discountApplied > 0 &&
    discountApplied <= invoiceAmount &&
    !errors["discountAmount"];

  const shouldShowFeePreview =
    feeValue && feeApplied > 0 && !errors["feeAmount"];

  const handleEditWrapper = (data: any) => {
    const payload: InvoiceAdjustmentPayload = {};

    if (Number(discountApplied) > 0) {
      payload.discountAmount = Number(discountApplied);
      payload.discountNote = data.discountNote;
      payload.discountType = discountType;
      payload.discountValue = discountValue;
    }

    if (Number(feeApplied) > 0) {
      payload.feeAmount = Number(feeApplied);
      payload.feeNote = data.feeNote;
      payload.feeType = feeType;
      payload.feeValue = feeValue;
    }

    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => Boolean(v)),
    );

    handleEdit(cleanedPayload);
  };

  return (
    <Box
      onSubmit={handleSubmit(handleEditWrapper)}
      component={"form"}
      id={id}
      noValidate
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Grid container size={12} spacing={2}>
        <Grid size={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Invoice Amount: {formatCurrency(invoiceAmount)}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Discount
          </Typography>

          <ToggleButtonGroup
            value={discountType}
            exclusive
            onChange={(_, val) => val !== null && setDiscountType(val)}
            size="small"
            className="segmented-toggle"
          >
            <ToggleButton value="amount">$</ToggleButton>
            <ToggleButton value="percentage">%</ToggleButton>
          </ToggleButtonGroup>

          <Grid container direction={"column"} gap={2}>
            <TextField
              label={
                discountType === "amount" ? "Discount Amount" : "Discount %"
              }
              name="discountAmount"
              type="number"
              register={register}
              onChange={(e) => setDiscountValue(e.target.value)}
              error={errors["discountAmount"]}
              slotProps={{
                input: {
                  startAdornment:
                    discountType === "amount" ? (
                      <InputAdornment position="start">$</InputAdornment>
                    ) : null,
                  endAdornment:
                    discountType === "percentage" ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : null,
                },
              }}
            />

            <TextField
              label="Discount Note"
              name="discountNote"
              register={register}
              multiline
              rows={3}
              maxLength={200}
              error={errors["discountNote"]}
            />
          </Grid>
        </Grid>

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Fee
          </Typography>

          <ToggleButtonGroup
            value={feeType}
            exclusive
            onChange={(_, val) => val !== null && setFeeType(val)}
            size="small"
            className="segmented-toggle"
          >
            <ToggleButton value="amount">$</ToggleButton>
            <ToggleButton value="percentage">%</ToggleButton>
          </ToggleButtonGroup>

          <Grid container direction={"column"} gap={2}>
            <TextField
              name="feeAmount"
              label={feeType === "amount" ? "Fee Amount" : "Fee %"}
              type="number"
              register={register}
              error={errors["feeAmount"]}
              onChange={(e) => setFeeValue(e.target.value)}
              slotProps={{
                input: {
                  startAdornment:
                    feeType === "amount" ? (
                      <InputAdornment position="start">$</InputAdornment>
                    ) : null,
                  endAdornment:
                    feeType === "percentage" ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : null,
                },
              }}
            />

            <TextField
              label="Fee Note"
              name="feeNote"
              register={register}
              multiline
              rows={3}
              maxLength={200}
              error={errors["feeNote"]}
            />
          </Grid>
        </Grid>
      </Grid>

      {!(discountApplied || feeApplied) && errors.commonError?.message && (
        <>
          <Divider sx={{ my: 1 }} />
          <FormHelperText
            sx={{
              color: "#AA210E",
            }}
          >
            {errors["commonError"]?.message}
          </FormHelperText>
        </>
      )}
      {(shouldShowDiscountPreview || shouldShowFeePreview) && (
        <>
          <Divider sx={{ my: 1 }} />
          {shouldShowDiscountPreview && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Discount to be applied:{" "}
              <strong>{formatCurrency(discountApplied)}</strong>
            </Typography>
          )}

          {shouldShowFeePreview && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Fee to be applied: <strong>{formatCurrency(feeApplied)}</strong>
            </Typography>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Final Amount:{" "}
            <span style={{ color: "#388e3c" }}>
              {formatCurrency(finalAmount)}
            </span>
          </Typography>
        </>
      )}
    </Box>
  );
};
